import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Button from './ui/Button';
import { ShoppingBag, Sparkles, Truck, Shield, Check } from 'lucide-react';
import styled from 'styled-components';
import { float, floatDelayed, pulse } from '../styles/animations';

const productUrl = 'https://trendykool.com/product/sky-blue-quinceanera-dresses/';

const Section = styled.section`
  padding: ${props => props.theme.spacing['4xl']} 0;
  background: linear-gradient(
    135deg,
    hsl(200, 80%, 50%) 0%,
    hsl(210, 75%, 55%) 50%,
    hsl(220, 70%, 50%) 100%
  );
  position: relative;
  overflow: hidden;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    padding: 8rem 0;
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const FloatingElement = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  animation: ${float} 8s ease-in-out infinite;
  
  &:nth-child(1) {
    top: 2.5rem;
    left: 2.5rem;
    width: 12rem;
    height: 12rem;
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:nth-child(2) {
    bottom: 2.5rem;
    right: 2.5rem;
    width: 16rem;
    height: 16rem;
    background: ${props => props.theme.colors.accent.main}33;
    animation: ${floatDelayed} 10s ease-in-out infinite;
  }
`;

const SparkleContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const SparkleIcon = styled(Sparkles)`
  position: absolute;
  color: rgba(255, 255, 255, 0.3);
  animation: ${pulse} 2s ease-in-out infinite;
  
  &:nth-child(1) {
    top: 25%;
    left: 20%;
    width: 1.5rem;
    height: 1.5rem;
  }
  
  &:nth-child(2) {
    bottom: 33%;
    right: 25%;
    width: 1.25rem;
    height: 1.25rem;
    animation-delay: 1s;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing['2xl']};
  position: relative;
  z-index: 10;
`;

const ContentWrapper = styled.div`
  max-width: 64rem;
  margin: 0 auto;
  text-align: center;
`;

const Title = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.display};
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: white;
  margin-bottom: ${props => props.theme.spacing['2xl']};
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize['5xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['6xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['7xl']};
  }
  
  @media (min-width: ${props => props.theme.breakpoints['2xl']}) {
    font-size: ${props => props.theme.typography.fontSize['8xl']};
  }
`;

const Description = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${props => props.theme.typography.fontSize.lg};
  line-height: 1.7;
  margin-bottom: 2.5rem;
  max-width: 42rem;
  margin-left: auto;
  margin-right: auto;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.typography.fontSize.xl};
    max-width: 48rem;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    max-width: 56rem;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.xl}) {
    font-size: ${props => props.theme.typography.fontSize['3xl']};
    max-width: 64rem;
  }
`;

const CTAButtonWrapper = styled.div`
  display: inline-block;
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

const TrustBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${props => props.theme.spacing['2xl']};
  color: rgba(255, 255, 255, 0.8);
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: white;
  }
`;

const CTASection = () => {
  const { t } = useLanguage();

  return (
    <Section>
      <FloatingElements>
        <FloatingElement />
        <FloatingElement />
      </FloatingElements>

      <SparkleContainer>
        <SparkleIcon />
        <SparkleIcon />
      </SparkleContainer>

      <Container>
        <ContentWrapper>
          <Title>{t('cta.title')}</Title>
          <Description>{t('cta.description')}</Description>

          <CTAButtonWrapper>
            <a href={productUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="heroInverse" size="xl">
                <ShoppingBag style={{ width: '1.25rem', height: '1.25rem' }} />
                <span>{t('cta.button')}</span>
              </Button>
            </a>
          </CTAButtonWrapper>

          <TrustBadges>
            <TrustBadge>
              <Check />
              <span>Free Customization</span>
            </TrustBadge>
            <TrustBadge>
              <Shield />
              <span>Perfect Fit Guarantee</span>
            </TrustBadge>
            <TrustBadge>
              <Truck />
              <span>Ships Worldwide</span>
            </TrustBadge>
          </TrustBadges>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default CTASection;
